import * as XLSX from 'xlsx';
import { z } from 'zod';

/**
 * Generic XLSX/CSV parser. Reads first sheet → array of objects keyed by header row.
 * Caller passes a zod schema to validate each row.
 */
export interface ParseRow<T> {
  ok: boolean;
  row: number; // 1-indexed (matches spreadsheet view)
  data?: T;
  errors?: string[];
  raw: Record<string, unknown>;
}

export interface ParseSummary<T> {
  rows: ParseRow<T>[];
  validCount: number;
  invalidCount: number;
  totalCount: number;
  sheetName: string;
}

export function parseSpreadsheet<S extends z.ZodTypeAny>(
  file: ArrayBuffer | Uint8Array,
  schema: S,
  opts?: { headerMap?: Record<string, string> },
): ParseSummary<z.output<S>> {
  type T = z.output<S>;
  const wb = XLSX.read(file, { type: 'array' });
  const sheetName = wb.SheetNames[0] ?? 'Sheet1';
  const ws = wb.Sheets[sheetName];
  if (!ws) {
    return { rows: [], validCount: 0, invalidCount: 0, totalCount: 0, sheetName };
  }
  const raw: Record<string, unknown>[] = XLSX.utils.sheet_to_json(ws, {
    defval: null,
    raw: true,
  });

  const headerMap = opts?.headerMap ?? {};
  const rows: ParseRow<T>[] = raw.map((r, i) => {
    const normalised: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(r)) {
      const mapped = headerMap[k] ?? k;
      normalised[mapped] = v;
    }
    const result = schema.safeParse(normalised);
    if (result.success) {
      return { ok: true, row: i + 2, data: result.data, raw: normalised };
    }
    return {
      ok: false,
      row: i + 2,
      raw: normalised,
      errors: result.error.issues.map(
        (iss) => `${iss.path.join('.') || 'row'}: ${iss.message}`,
      ),
    };
  });

  return {
    rows,
    validCount: rows.filter((r) => r.ok).length,
    invalidCount: rows.filter((r) => !r.ok).length,
    totalCount: rows.length,
    sheetName,
  };
}

/** Build a sheet from an array of objects with explicit header order. */
export function buildSheet<T extends Record<string, unknown>>(
  rows: T[],
  headers: (keyof T & string)[],
  sheetName = 'Sheet1',
): XLSX.WorkBook {
  const aoa = [headers as string[], ...rows.map((r) => headers.map((h) => r[h] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return wb;
}

export function workbookToArrayBuffer(wb: XLSX.WorkBook): ArrayBuffer {
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

export function downloadWorkbook(wb: XLSX.WorkBook, filename: string): void {
  XLSX.writeFile(wb, filename);
}
